/*
 * The admin site's words in English: the releaseNotes section (S48, extended to the
 * admin site 2026-10-29). Filled in from the components it serves: the release-note
 * list, the new-note page, the editor, its Properties box and the Version field.
 *
 * The noun the article editor's shared components take ('release note') is not
 * here: it is an argument those components choose their own sentences by.
 */

import type { AdminReleaseNoteSort, AdminReleaseNoteStatusFilter } from '@accotool-kb/shared';

export const releaseNotesEn = {
  /** Words more than one of the release-note pages uses. */
  common: {
    releaseNotes: 'Release notes',
    newReleaseNote: 'New release note',
    /** A note with no revision has no title. */
    untitled: '(untitled)',
    title: 'Title',
    properties: 'Properties',
    /** releaseNoteTitleError: the title rule, in the words releaseNoteTitleSchema answers with. */
    titleRequired: 'A title is required.',
    titleSingleLine: 'A release note title is a single line.',
  },

  /** ReleaseNoteListPage. */
  list: {
    matching: (count: number) => `${count} matching`,
    /** After a delete: the note's label, then the server's own account of what went. */
    deleted: (label: string, performed: string) => `Deleted "${label}". ${performed}`,
    search: 'Search',
    searchPlaceholder: 'Title or version…',
    state: 'State',
    product: 'Product',
    anyProduct: 'Any',
    sort: 'Sort',
    sorts: {
      modified: 'Last edited',
      created: 'Newest',
      publish: 'Publish date',
      title: 'Title',
    } satisfies Record<AdminReleaseNoteSort, string>,
    /** The State filter's saved views, by the value the server parses. */
    statuses: {
      all: 'All release notes',
      live: 'Live',
      scheduled: 'Scheduled',
      hidden: 'Hidden',
      draft: 'Unpublished draft',
      withdrawn: 'Text withdrawn',
      unwritten: 'Not written yet',
    } satisfies Record<AdminReleaseNoteStatusFilter, string>,
    loading: 'Loading release notes…',
    noneMatch: 'No release notes match',
    noneMatchHint: 'Change the filters, or write the first release note.',
    columnReleaseNote: 'Release note',
    columnState: 'State',
    columnPublish: 'Publish',
    columnEdited: 'Edited',
    columnActions: 'Actions',
    /** The marker for a revision sitting ahead of the published one, and its tooltip. */
    draft: 'draft',
    draftNewer: (latest: number | null, current: number | null) =>
      `Revision ${latest} is newer than the published revision ${current ?? '—'}.`,
    edit: 'Edit',
    delete: 'Delete',
  },

  /** NewReleaseNotePage. */
  create: {
    loadingMetadata: 'Loading metadata…',
    setUpMetadata: 'Set up the metadata first',
    needsProduct:
      'A release note needs a product - it is half of the note’s address - and no product tags exist yet.',
    goToMetadata: 'Go to Metadata',
    /** The breadcrumb's last step. */
    breadcrumbNew: 'New',
    intro:
      'The release note is created with its first revision, so it has something to show the moment it is published.',
    content: 'Content',
    /** The same words the server answers with, from releaseNotePropertiesSchema. */
    productRequired: 'Assign a product.',
    publishing: 'Publishing',
    whenVisible: 'When should readers see it',
    publishModes: {
      hidden: 'Keep it hidden for now',
      now: 'Publish immediately',
      schedule: 'Schedule for later',
    },
    publishModeHints: {
      hidden: 'Nothing is visible to readers until you publish it.',
      now: 'Readable as soon as it is created.',
      schedule: 'Becomes readable at the chosen instant.',
    },
    publishAt: 'Publish at (your local time)',
    /** {iso} is the instant as it will be stored, in UTC. */
    storedAs: (iso: string) => `Stored as ${iso}`,
    creating: 'Creating…',
    createReleaseNote: 'Create release note',
    cancel: 'Cancel',
  },

  /** ReleaseNoteEditorPage. */
  editor: {
    loading: 'Loading the release note…',
    deleted: 'Deleted',
    deletedEverything: (label: string) => `“${label}” and everything belonging to it has been deleted.`,
    backToList: 'Back to release notes',
    /** Under the heading: the note's product and version, then two dates. */
    byline: '{product} {version} · created {created} · last activity {modified}',
    viewOnSite: 'View on site',
    deleteReleaseNote: 'Delete release note',
    savedAndPublished: (revision: number) => `Revision ${revision} saved and published.`,
    savedAsDraft: (revision: number, readersSee: number | null) =>
      `Revision ${revision} saved as a draft. Readers still see revision ${readersSee ?? '—'}.`,
    /** ArticleSaveBox's heading and change-note placeholder, for a release note. */
    saveBoxHeading: 'Release note',
    changeNotePlaceholder: 'e.g. Added the fix for the export timeout',
    outOfDate: 'Your copy is out of date.',
    outOfDateHelp:
      'Copy your text somewhere safe, reload the page, and re-apply your change on top of theirs. Nothing has been overwritten.',
    text: 'Text',
    draftBadge: (revision: number | null) => `draft #${revision}`,
    nothingWritten: 'nothing written yet',
    editingDraft: (latest: number | null, current: number | null) =>
      `You are editing draft revision ${latest}; readers are getting revision ${current ?? '—'}.`,
    publishToShow: 'Save & publish to make this the version readers see.',
  },

  /** ReleaseNoteProperties. */
  properties: {
    addressMoved: (next: string, previous: string) =>
      `The address is now ${next}. Links to ${previous} written by hand elsewhere no longer lead here.`,
    /** {address} is the public address being built, as code. */
    address: 'Address: {address}',
    addressMoving: 'Address: {address} — changing the product or the version moves the note to this address.',
    saving: 'Saving…',
    save: 'Save properties',
    unsaved: 'Unsaved changes',
  },

  /** ReleaseNoteVersionInput. */
  versionInput: {
    label: 'Version',
    hint: 'The v is part of the version and is already there: type the four numbers after it, for example 1.2.3.4.',
  },
};
